import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Brain, 
  BookOpen, 
  Target, 
  TrendingUp, 
  Zap, 
  Shield,
  Sparkles,
  Users
} from 'lucide-react';
import './NewHomePage.css';

const NewHomePage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [navScrolled, setNavScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/waitlist/join', { email });
      
      if (response.data.success) {
        setSuccess(true);
        setEmail('');
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError("You're already on the waitlist! 🎉");
      } else {
        setError(err.response?.data?.message || 'Oops! Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const exams = [
    'JEE', 'NEET', 'UPSC', 'CAT', 'GATE', 'SSC', 'Banking', 'Railway', 
    'NDA', 'CLAT', 'IAS', 'IBPS', 'RRB', 'Defence', 'State PSC',
    'SAT', 'ACT', 'GRE', 'GMAT', 'TOEFL', 'IELTS', 'MCAT', 'LSAT',
    'AP Exams', 'IB Exams', 'A-Levels', 'PSAT', 'USMLE', 'CFA'
  ];

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Learning',
      description: 'Advanced AI system that understands NCERT curriculum and adapts to your learning pace'
    },
    {
      icon: BookOpen,
      title: 'NCERT Aligned',
      description: 'Every answer grounded in official textbooks with precise chapter and page references'
    },
    {
      icon: Target,
      title: 'Smart Analytics',
      description: 'Chapter-wise weak area detection and personalized improvement roadmap'
    },
    {
      icon: TrendingUp,
      title: 'PYQ Database',
      description: 'Comprehensive previous year questions with detailed AI-generated solutions'
    },
    {
      icon: Zap,
      title: 'Mock Tests',
      description: 'AI-generated prediction papers that simulate actual exam patterns'
    },
    {
      icon: Shield,
      title: 'Trusted by Thousands',
      description: 'Join students across India who trust VidyaTid for their exam preparation'
    }
  ];

  const plans = [
    {
      name: 'Free',
      price: '₹0',
      period: 'forever',
      features: [
        'Limited AI queries per day',
        'Basic chapter practice',
        'NCERT reference access',
        'Community support'
      ],
      cta: 'Join Waitlist',
      highlighted: false
    },
    {
      name: 'Pro',
      price: '₹499',
      period: 'per month',
      badge: 'Most Popular',
      features: [
        'Unlimited AI tutor queries',
        'Personalized mock tests',
        'AI prediction papers',
        'Advanced analytics dashboard',
        'Priority support',
        'Download study materials'
      ],
      cta: 'Join Waitlist',
      highlighted: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'for institutions',
      features: [
        'Everything in Pro',
        'Bulk student accounts',
        'Custom branding',
        'Dedicated account manager',
        'Advanced reporting',
        'API access'
      ],
      cta: 'Contact Sales',
      highlighted: false
    }
  ];

  return (
    <div className="new-homepage">
      {/* Navigation */}
      <nav className={`navbar ${navScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-name">VidyaTid</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            World's Most <span className="gradient-text">Intelligent</span> Exam Preparation Platform
          </motion.h1>

          <motion.p 
            className="hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            AI-powered learning system for competitive exam preparation. Master multiple examinations with personalized AI guidance and adaptive learning technology.
          </motion.p>

          <motion.form 
            id="hero-form"
            className="hero-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {!success ? (
              <>
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    className={error ? 'error' : ''}
                  />
                  <button type="submit" disabled={loading}>
                    {loading ? 'Joining...' : 'Join Waitlist →'}
                  </button>
                </div>
                {error && <p className="form-error">{error}</p>}
                <p className="form-hint">Join the waitlist for early access</p>
              </>
            ) : (
              <div className="success-message">
                ✓ You're on the list! Check your email for confirmation.
              </div>
            )}
          </motion.form>
        </div>
      </section>

      {/* Exams Section */}
      <section id="exams" className="exams-section">
        <div className="section-container">
          <h2 className="section-title">
            Prepare for <span className="gradient-text">Multiple</span> Exams
          </h2>
          <p className="section-subtitle">
            One platform, unlimited possibilities. We're expanding to cover all major competitive examinations worldwide.
          </p>
          <div className="exam-scroll-container">
            <div className="exam-scroll-track">
              {[...exams, ...exams].map((exam, index) => (
                <div key={index} className="exam-tag-scroll">
                  {exam}
                </div>
              ))}
            </div>
          </div>
          <p className="exams-footer">And many more coming soon...</p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div className="about-content">
              <h2 className="section-title">
                About <span className="gradient-text">VidyaTid</span>
              </h2>
              <p className="about-text">
                VidyaTid is a technology-first AI company revolutionizing competitive exam preparation worldwide. We're not a traditional coaching institute – we're an intelligent learning system powered by advanced artificial intelligence.
              </p>
              <p className="about-text">
                Founded with the mission to make structured, syllabus-aligned, AI-powered academic assistance accessible to every student globally, we've rapidly expanded to cover 15+ major competitive examinations across multiple domains.
              </p>
              <p className="about-text">
                Our platform combines cutting-edge AI technology with comprehensive curriculum understanding to provide personalized learning experiences that adapt to each student's unique preparation journey and learning style.
              </p>
              <div className="about-stats">
                <div className="about-stat">
                  <Users size={24} className="about-stat-icon" />
                  <span className="about-stat-text">Global Reach</span>
                </div>
                <div className="about-stat">
                  <Target size={24} className="about-stat-icon" />
                  <span className="about-stat-text">15+ Exam Categories</span>
                </div>
              </div>
            </div>
            <div className="about-visual">
              <div className="vision-box">
                <div className="vision-icon">
                  <Brain size={64} strokeWidth={1.5} />
                </div>
                <h3 className="vision-title">Our Vision</h3>
                <p className="vision-quote">
                  "To become the world's most trusted AI-powered learning platform, transforming how students prepare for competitive examinations through intelligent, personalized education technology"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Transform Your Exam Preparation?</h2>
          <p className="cta-subtitle">
            Join thousands of learners who are already learning smarter with AI
          </p>
          <form className="cta-form" onSubmit={handleSubmit}>
            {!success ? (
              <>
                <div className="cta-input-group">
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <button type="submit" disabled={loading}>
                    {loading ? 'Joining...' : 'Join Waitlist →'}
                  </button>
                </div>
                {error && <p className="cta-error">{error}</p>}
              </>
            ) : (
              <div className="cta-success">
                ✓ You're on the list! We'll notify you when we launch.
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-col footer-brand">
              <span className="footer-logo-name">VidyaTid</span>
              <p className="footer-tagline">
                Transforming competitive exam preparation worldwide through intelligent, AI-powered education technology.
              </p>
              <div className="footer-social">
                <a href="https://x.com/vidyatid" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/vidyatid.in/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.reddit.com/user/Commercial-Sleep-903" target="_blank" rel="noopener noreferrer" aria-label="Reddit">
                  <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div className="footer-col">
              <h4 className="footer-title">Product</h4>
              <ul className="footer-links">
                <li><a href="#exams">Exams</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#about">Features</a></li>
                <li><a href="#about">Pricing</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="footer-title">Company</h4>
              <ul className="footer-links">
                <li><a href="#about">About Us</a></li>
                <li><a href="#about">Careers</a></li>
                <li><a href="#about">Blog</a></li>
                <li><a href="#about">Contact</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="footer-title">Legal</h4>
              <ul className="footer-links">
                <li><a href="#about">Privacy Policy</a></li>
                <li><a href="#about">Terms of Service</a></li>
                <li><a href="#about">Refund Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">© 2026 VidyaTid. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewHomePage;
